<template>
  <div class="admin-users">
    <el-card>
      <div slot="header" class="card-header">
        <span>{{ $t('adminUsers.title') }}</span>
        <el-button type="primary" size="small" @click="handleAdd">{{ $t('adminUsers.add') }}</el-button>
      </div>

      <!-- 管理员列表 -->
      <el-table :data="list" border stripe v-loading="loading">
        <el-table-column prop="id" :label="$t('adminUsers.colId')" width="80"></el-table-column>
        <el-table-column prop="email" :label="$t('adminUsers.colEmail')"></el-table-column>
        <el-table-column prop="name" :label="$t('adminUsers.colName')"></el-table-column>
        <el-table-column prop="phone" :label="$t('adminUsers.colPhone')" width="130"></el-table-column>
        <!-- role / status 在库里都是 unsignedTinyInteger（0/1），
             不能用字符串 'super_admin' / 'active' 比较，否则永远不成立、标签显示错误 -->
        <el-table-column prop="role" :label="$t('adminUsers.colRole')" width="100">
          <template slot-scope="scope">
            <el-tag :type="Number(scope.row.role) === 1 ? 'primary' : 'info'">
              {{ Number(scope.row.role) === 1 ? $t('adminUsers.roleAdmin') : $t('adminUsers.roleUser') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" :label="$t('adminUsers.colStatus')" width="80">
          <template slot-scope="scope">
            <el-tag :type="Number(scope.row.status) === 1 ? 'success' : 'info'">
              {{ Number(scope.row.status) === 1 ? $t('adminUsers.statusActive') : $t('adminUsers.statusInactive') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" :label="$t('adminUsers.colCreated')" width="170"></el-table-column>
        <el-table-column :label="$t('adminUsers.colAction')" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="handleEdit(scope.row)">{{ $t('adminUsers.edit') }}</el-button>
            <el-button type="text" style="color: #F56C6C;" @click="handleDelete(scope.row)">{{ $t('adminUsers.delete') }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        class="pagination"
        @current-change="handlePageChange"
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        layout="total, prev, pager, next"
      ></el-pagination>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogType === 'add' ? $t('adminUsers.dialogAdd') : $t('adminUsers.dialogEdit')"
      :visible.sync="dialogVisible"
      width="500px"
    >
      <el-form ref="adminForm" :model="adminForm" :rules="adminRules" label-width="100px">
        <el-form-item :label="$t('adminUsers.labelEmail')" prop="email">
          <el-input v-model="adminForm.email" :placeholder="$t('adminUsers.placeholderEmail')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('adminUsers.labelPassword')" :prop="dialogType === 'add' ? 'password' : ''">
          <el-input v-model="adminForm.password" type="password" :placeholder="$t('adminUsers.placeholderPassword')" show-password></el-input>
        </el-form-item>
        <el-form-item :label="$t('adminUsers.labelName')" prop="name">
          <el-input v-model="adminForm.name" :placeholder="$t('adminUsers.placeholderName')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('adminUsers.labelPhone')" prop="phone">
          <el-input v-model="adminForm.phone" :placeholder="$t('adminUsers.placeholderPhone')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('adminUsers.labelRole')" prop="role">
          <el-select v-model="adminForm.role" :placeholder="$t('adminUsers.placeholderRole')">
            <el-option :label="$t('adminUsers.roleOptionAdmin')" :value="1"></el-option>
            <el-option :label="$t('adminUsers.roleOptionUser')" :value="0"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('adminUsers.labelStatus')">
          <!-- 值必须是数字 1/0，与 users.status 列类型一致。
               原先用 'active'/'inactive' 字符串会被 MySQL 静默转成 0，等于每次编辑都禁用账号 -->
          <el-switch
            v-model="adminForm.status"
            :active-value="1"
            :inactive-value="0"
            :active-text="$t('adminUsers.switchActive')"
            :inactive-text="$t('adminUsers.switchInactive')"
          ></el-switch>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">{{ $t('adminUsers.cancel') }}</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleSubmit">{{ $t('adminUsers.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getList, save, update, deleteAdmin } from '@/api/adminUsers'

export default {
  name: 'AdminUsers',
  data() {
    return {
      list: [],
      loading: false,
      page: 1,
      pageSize: 20,
      total: 0,
      dialogVisible: false,
      dialogType: 'add',
      submitLoading: false,
      // role / status 用数字，与 users 表的 unsignedTinyInteger 列保持一致
      // （0/1，而非 'admin'/'active' 这类字符串）
      adminForm: {
        email: '',
        password: '',
        name: '',
        phone: '',
        role: 1,
        status: 1
      },
      adminRules: {
        email: [
          { required: true, message: this.$t('adminUsers.ruleEmail'), trigger: 'blur' },
          { type: 'email', message: this.$t('adminUsers.ruleEmailFormat'), trigger: 'blur' }
        ],
        password: [
          { required: true, message: this.$t('adminUsers.rulePassword'), trigger: 'blur' },
          { min: 6, message: this.$t('adminUsers.rulePasswordLen'), trigger: 'blur' }
        ],
        name: [
          { required: true, message: this.$t('adminUsers.ruleName'), trigger: 'blur' }
        ],
        role: [
          { required: true, message: this.$t('adminUsers.ruleRole'), trigger: 'change' }
        ]
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    // 加载管理员列表
    loadData() {
      this.loading = true
      getList({ page: this.page, pageSize: this.pageSize }).then(res => {
        this.list = res.data.list || []
        this.total = res.data.total || 0
      }).catch(() => {
        this.$message.error(this.$t('adminUsers.loadFail'))
      }).finally(() => {
        this.loading = false
      })
    },
    // 新增管理员
    handleAdd() {
      this.dialogType = 'add'
      this.adminForm = {
        email: '',
        password: '',
        name: '',
        phone: '',
        role: 1,
        status: 1
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.adminForm && this.$refs.adminForm.clearValidate()
      })
    },
    // 编辑管理员
    handleEdit(row) {
      this.dialogType = 'edit'
      // 列表接口返回的是数据库原始值，需要先规整成表单期望的类型再回填：
      //   · phone 可能为 null（列为 NOT NULL default ''），直接提交会报
      //     "Column 'phone' cannot be null"
      //   · role / status 是 0/1 数字，必须转成数字，否则 switch 找不到匹配值会显示为关闭状态
      this.adminForm = {
        ...row,
        password: '',
        phone: row.phone == null ? '' : String(row.phone),
        role: Number(row.role) === 1 ? 1 : 0,
        status: Number(row.status) === 1 ? 1 : 0
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.adminForm && this.$refs.adminForm.clearValidate()
      })
    },
    // 提交表单
    handleSubmit() {
      this.$refs.adminForm.validate(valid => {
        if (!valid) return
        this.submitLoading = true
        const api = this.dialogType === 'add' ? save(this.adminForm) : update(this.adminForm.id, this.adminForm)
        api.then(() => {
          this.$message.success(this.dialogType === 'add' ? this.$t('adminUsers.addSuccess') : this.$t('adminUsers.editSuccess'))
          this.dialogVisible = false
          this.loadData()
        }).catch(() => {
          this.$message.error(this.$t('adminUsers.opFail'))
        }).finally(() => {
          this.submitLoading = false
        })
      })
    },
    // 删除管理员
    async handleDelete(row) {
      try {
        await this.$confirm(this.$t('adminUsers.deleteConfirm'), this.$t('common.tip'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
        deleteAdmin(row.id).then(() => {
          this.$message.success(this.$t('adminUsers.deleteSuccess'))
          this.loadData()
        }).catch(() => {
          this.$message.error(this.$t('adminUsers.deleteFail'))
        })
      } catch (e) {
        // 取消删除
      }
    },
    // 分页切换
    handlePageChange(page) {
      this.page = page
      this.loadData()
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  font-size: 16px;
}
.pagination {
  margin-top: 20px;
  text-align: right;
}
</style>
